import React, { createContext, useContext, useEffect, useState } from 'react';
import { StatusCode } from '../../components/Alert.tsx';
import MeasurementUnitsController from '../../controllers/MeasurementUnitsController.ts';
import { MeasurementUnit } from '../../models/measurementUnits/measurementUnit.ts';
import { BaseSearchParam } from '../../models/searchParams/baseSearchParam.ts';
import { TableSearchParam } from '../../models/searchParams/tableSearchParam.ts';
import { useAlert } from '../alertProvider.tsx';

type MeasurementUnitsContextType = {
    isLoaded?: boolean;
    measurementUnits: MeasurementUnit[];
    totalCount: number;
    tableSearchParam: TableSearchParam;
    setTableSearchParam: (tableSearchParam: TableSearchParam) => void;
    searchParam: BaseSearchParam;
    setSearchParam: (baseSearchParam: BaseSearchParam) => void;
    deleteMeasurementUnit: (measurementUnitID: number) => {};
};

const MeasurementUnitsContext = createContext<MeasurementUnitsContextType>({} as MeasurementUnitsContextType);

export const MeasurementUnitsContextProvider = ({ children }: { children: any }) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [measurementUnits, setMeasurementUnits] = useState<MeasurementUnit[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
    const [searchParam, setSearchParam] = useState<BaseSearchParam>({} as BaseSearchParam);
    const { showAlert } = useAlert();

    const fetchMeasurementUnits = async () => {
        try {
            setIsLoaded(false);
            const result = await MeasurementUnitsController.get(tableSearchParam, searchParam);
            setMeasurementUnits(result.measurementUnits);
            setTotalCount(result.totalCount);
        } catch (error) {
            console.error('Error fetching measurementUnits:', error);
        } finally {
            setIsLoaded(true);
        }
    };
    useEffect(() => {
        fetchMeasurementUnits();
    }, [tableSearchParam, searchParam]);

    const deleteMeasurementUnit = async (measurementUnitID: number) => {
        try {
            const result = await MeasurementUnitsController.delete(measurementUnitID);
            if (result.notificationStatus == StatusCode.success)
                fetchMeasurementUnits();
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error deleting measurementUnit:', error);
        } finally {
            setIsLoaded(true);
        }
    }

    const context: MeasurementUnitsContextType = {
        isLoaded,
        measurementUnits,
        totalCount,
        tableSearchParam,
        setTableSearchParam,
        searchParam,
        setSearchParam,
        deleteMeasurementUnit
    }

    return (
        <MeasurementUnitsContext.Provider value={context}>
            {children}
        </MeasurementUnitsContext.Provider>
    )
}

export const useMeasurementUnitsContext = () => useContext(MeasurementUnitsContext)
